import React, { Component } from 'react';
import styleable from 'react-styleable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { DatePicker, DataTable } from '@3plearning/react-components';
import cloneDeep from 'lodash/cloneDeep';
import { activeFiltersPropType } from '../../../components/report-filters/filter-prop-types';
import {
    SELECTED_DATE,
    SELECTED_GROUP,
    SELECTED_STUDENT,
    SELECTED_CLASS
} from '../../../components/report-filters/filter-constants';
import activeFiltersActions from '../../../components/report-filters/redux/active-filters/active-filters-actions';
import css from './single-class.scss';
import singleClassActions, {
    LIVE_MATHS_CLASS_RETRIEVED
} from './single-class-actions';
import TableHeader from '../../../components/table-header/table-header';
import {
    SINGLE_CLASS_TABLE_CONFIG,
    GROUP_NAME,
    STUDENT_NAME
} from './single-class-constants';
import singleClassPropTypes from './single-class-prop-types';
import activeFiltersSelectors from '../../../components/report-filters/redux/active-filters/active-filters-selectors';
import FilterHelper from '../../../helpers/filter-helper';
import { translateTableLabels } from '../../../helpers/locale-helpers';

/**
 * Displays live maths results for a single class
 */
export class SingleClass extends Component {
    static propTypes = {
        liveMathsData: singleClassPropTypes.liveMathsDataPropType,
        liveMathsLevel: PropTypes.number,
        activeFilters: activeFiltersPropType,
        getLiveMathsData: PropTypes.func,
        updateActiveFilter: PropTypes.func,
        invalidRequest: PropTypes.string
    };

    constructor(props) {
        super(props);
        this.state = {
            singleClassTableConfig: null,
            liveMathsLevelData: null
        };
        this.i18n = window.i18n;
    }

    componentDidMount() {
        const { activeFilters, liveMathsData, liveMathsLevel } = this.props;
        if (FilterHelper.shouldLoadSingleClass(activeFilters)) {
            this.getClassLiveMathsData(
                activeFilters[SELECTED_CLASS].id,
                activeFilters[SELECTED_DATE]
            );
            this.setLiveMathsData(
                liveMathsData,
                liveMathsLevel,
                activeFilters[SELECTED_GROUP].id
            );
        }
    }

    componentWillReceiveProps(newProps) {
        const {
            activeFilters,
            liveMathsData,
            liveMathsLevel,
            invalidRequest,
            updateActiveFilter
        } = this.props;
        const {
            activeFilters: newActiveFilters,
            liveMathsData: newLiveMathsData,
            liveMathsLevel: newLiveMathsLevel,
            invalidRequest: newInvalidRequest
        } = newProps;

        // user's selection has changed (date, class or group), need to request data from backend
        if (
            FilterHelper.shouldReloadSingleClass(
                activeFilters,
                newActiveFilters
            )
        ) {
            this.getClassLiveMathsData(
                newActiveFilters[SELECTED_CLASS].id,
                newActiveFilters[SELECTED_DATE]
            );
        }

        // we have received live maths data from backend or user has selected new level
        if (
            newLiveMathsData &&
            (liveMathsData !== newLiveMathsData ||
                liveMathsLevel !== newLiveMathsLevel)
        ) {
            this.setLiveMathsData(
                newLiveMathsData,
                newLiveMathsLevel,
                newActiveFilters[SELECTED_GROUP].id
            );
            this.setLiveMathsConfig(
                newLiveMathsData.classroomName,
                newActiveFilters[SELECTED_GROUP].id
            );
        }

        // request to backend failed, attempt to load page using default values
        if (
            invalidRequest !== newInvalidRequest &&
            newInvalidRequest === LIVE_MATHS_CLASS_RETRIEVED
        ) {
            if (
                newActiveFilters[SELECTED_DATE].type !==
                DatePicker.THIS_SCHOOL_YEAR
            ) {
                updateActiveFilter(SELECTED_DATE, {
                    type: DatePicker.THIS_SCHOOL_YEAR
                });
            }
        }
    }

    getClassLiveMathsData(classroomId, selectedDate) {
        this.props.getLiveMathsData(classroomId, selectedDate);
    }

    setLiveMathsData(allLevelsLiveMathsData, liveMathsLevel, groupId) {
        if (
            allLevelsLiveMathsData &&
            allLevelsLiveMathsData.groupLiveMathsLevelResult
        ) {
            groupId = +groupId || 0;
            let liveMathsLevelData =
                allLevelsLiveMathsData.groupLiveMathsLevelResult[
                    liveMathsLevel - 1
                ].groups;

            if (groupId) {
                liveMathsLevelData = liveMathsLevelData.filter(
                    (group) => group.id === +groupId
                );
            }

            this.setState({ liveMathsLevelData });
        }
    }

    setLiveMathsConfig(className, groupId) {
        groupId = +groupId || 0;
        let singleClassTableConfig = cloneDeep(SINGLE_CLASS_TABLE_CONFIG);
        const classNameColHeader = singleClassTableConfig.colGroups[0][0];
        const groupCol = this.getColFromConfig(
            singleClassTableConfig,
            GROUP_NAME
        );
        const studentCol = this.getColFromConfig(
            singleClassTableConfig.nestedData,
            STUDENT_NAME
        );

        classNameColHeader.name = className;
        if (!groupId) {
            classNameColHeader.sort = { colId: GROUP_NAME, sortDir: 'desc' };
            groupCol.onClick = (selectedGroup) =>
                this.loadNewPage(SELECTED_GROUP, selectedGroup);
        }
        studentCol.onClick = (selectedStudent) =>
            this.loadNewPage(SELECTED_STUDENT, selectedStudent);

        singleClassTableConfig = translateTableLabels(singleClassTableConfig);
        this.setState({ singleClassTableConfig });
    }

    getColFromConfig = (config, colId) =>
        config.cols.find((col) => col.id === colId);

    /**
     * Loads the group or student page when user clicks on item in table
     */
    loadNewPage = (filterName, selectedItem) => {
        this.props.updateActiveFilter(filterName, {
            id: selectedItem.id,
            name: selectedItem.name
        });
    };

    render() {
        const { liveMathsLevelData, singleClassTableConfig } = this.state;

        return (
            <div className={css.component}>
                <div className={css['live-maths-results']}>
                    <TableHeader
                        title={'Table Header - Live Mathletics'}
                        translate
                    />
                    <DataTable
                        config={singleClassTableConfig}
                        data={liveMathsLevelData}
                    />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        activeFilters: activeFiltersSelectors.selectActiveFilters(state),
        liveMathsData: state.liveMathsSingleClass,
        liveMathsLevel: state.liveMathsLevel,
        invalidRequest: state.invalidRequest
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            getLiveMathsData: singleClassActions.getLiveMathsData,
            updateActiveFilter: activeFiltersActions.updateActiveFilter
        },
        dispatch
    );
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(styleable(css)(SingleClass));



// WEBPACK FOOTER //
// ./containers/live-maths/single-class/single-class.js